import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api, ApiClientError } from '../lib/api';
import { Banner, TextInput } from '../components/ui';
import { homeFor } from '../lib/routes';

export default function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const session = await signIn(email.trim().toLowerCase(), password);
      // An admin who never finished OTP has no business in the console yet.
      if (session.nextStep === 'verify_mobile') {
        setError('Finish verifying your mobile number in the app before signing in here.');
        return;
      }
      if (session.user.role === 'sales_rep') {
        setError('The console is for admins and managers. Reps use the Salestrendz app.');
        return;
      }
      navigate(homeFor(session.user.role), { replace: true });
    } catch (err) {
      setError(err instanceof ApiClientError
        ? err.message
        : 'Could not reach the server. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  async function onForgot() {
    setError(null);
    setNotice(null);
    const addr = email.trim().toLowerCase();
    if (!addr || !addr.includes('@')) {
      setError('Enter your email above first, then tap Forgot password.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/password/forgot', { email: addr });
      setNotice('If that email is registered, a password-reset link is on its way.');
    } catch {
      setNotice('If that email is registered, a password-reset link is on its way.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="row gap-12" style={{ marginBottom: 20, justifyContent: 'center' }}>
          <div className="brand-mark" style={{ width: 36, height: 36, fontSize: 17 }}>S</div>
          <span className="brand-name" style={{ fontSize: 19 }}>Salestrendz</span>
        </div>

        <div className="card">
          <h1 style={{ fontSize: 21 }}>Sign in</h1>
          <p className="muted small" style={{ marginTop: 5, marginBottom: 20 }}>
            Admin console for your team
          </p>

          <form onSubmit={onSubmit} className="col gap-16">
            {error ? <Banner kind="danger">{error}</Banner> : null}
            {notice ? <Banner kind="success">{notice}</Banner> : null}

            <TextInput
              label="Work email"
              type="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              placeholder="you@company.com"
              onChange={(e) => setEmail(e.target.value)}
            />

            <TextInput
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              placeholder="••••••••"
              onChange={(e) => setPassword(e.target.value)}
            />

            <div style={{ textAlign: 'right', marginTop: -6 }}>
              <button type="button" className="btn-link" onClick={onForgot} disabled={busy}
                style={{ background: 'none', border: 0, color: 'var(--brand)', cursor: 'pointer', fontSize: 13, padding: 0 }}>
                Forgot password?
              </button>
            </div>

            <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={busy}>
              {busy ? <span className="spinner" /> : null}
              {busy ? 'Signing in' : 'Sign in'}
            </button>
          </form>
        </div>

        <p className="center small faint" style={{ marginTop: 16 }}>
          Field reps sign in on the Salestrendz mobile app.
        </p>
      </div>
    </div>
  );
}
